import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import {
  Box, Button, CircularProgress, Container,
  Dialog, DialogActions, DialogContent, DialogTitle,
  IconButton, Paper, Stack, Tab, Tabs, TextField, Typography,
} from "@mui/material";
import { MdAdd, MdDelete, MdEdit, MdRefresh, MdSearch } from "react-icons/md";
import {
  deleteItem, getAcademyItem,
  setRecommended, setEvents, setSuccess,
} from "../store/actions/items";
import AddItemsDialog from "../components/modals/AddItemsDialog";
import ItemsEditModal from "../components/modals/ItemsEditModal";
import Colors from "../../assets/styles/Colors";

const TABS = ["Recomendado", "Eventos", "Sistema de Exito"];

const CATEGORY_MAP = ["recommended", "events", "success"];

function fmtDate(v) {
  if (!v?.seconds) return "—";
  const d = new Date(v.seconds * 1000 + (v.nanoseconds ?? 0) / 1e6);
  return isNaN(d) ? "—" : d.toLocaleDateString();
}

export default function ItemsView() {
  const dispatch = useDispatch();
  const { recommended, events, success } = useSelector((s) => s.items);

  const isFirstUse = useRef(true);
  const [loading, setLoading]   = useState(true);
  const [tabValue, setTabValue] = useState(0);
  const [search, setSearch]     = useState("");
  const [addOpen, setAddOpen]   = useState(false);

  const [editModalOpen, setEditModalOpen] = useState(false);
  const [selectedItem, setSelectedItem]   = useState(null);

  const [confirmDelete, setConfirmDelete] = useState(null);
  const [deleting, setDeleting]           = useState(false);

  const getItemsFromFB = useCallback(async () => {
    setLoading(true);
    try {
      await dispatch(setRecommended());
      await dispatch(setEvents());
      await dispatch(setSuccess());
    } catch (e) {
      console.error(e);
    } finally {
      setLoading(false);
    }
  }, [dispatch]);

  useEffect(() => {
    if (isFirstUse.current) {
      isFirstUse.current = false;
      getItemsFromFB();
    }
  }, [getItemsFromFB]);

  const currentData = useMemo(() => {
    if (tabValue === 0) return recommended || [];
    if (tabValue === 1) return events      || [];
    return success || [];
  }, [tabValue, recommended, events, success]);

  const filtered = useMemo(() => {
    if (!search.trim()) return currentData;
    const q = search.toLowerCase();
    return currentData.filter((item) => (item.name || "").toLowerCase().includes(q));
  }, [currentData, search]);

  const handleOpenEditModal = async (itemId) => {
    const item = await getAcademyItem(itemId);
    setSelectedItem(item);
    setEditModalOpen(true);
  };

  const handleCloseEditModal = () => {
    setSelectedItem(null);
    setEditModalOpen(false);
  };

  const confirmDeleteAction = async () => {
    if (!confirmDelete) return;
    setDeleting(true);
    try {
      await deleteItem(confirmDelete.id);
      setConfirmDelete(null);
      getItemsFromFB();
    } catch (err) {
      console.error("Error deleting item:", err);
    } finally {
      setDeleting(false);
    }
  };

  // Show thumbnail for image columns
  const showDate = tabValue !== 2; // "Sistema de Exito" has no cdate

  return (
    <Box sx={{ width: "100%", py: 3 }}>
      <Container maxWidth="xl">

        {/* Header */}
        <Paper sx={{ mb: 3, p: 2.5, borderRadius: 3, background: `linear-gradient(135deg, ${Colors.bgSecondary} 0%, ${Colors.bgTertiary} 100%)`, border: `1px solid ${Colors.borderSecondary}` }}>
          <Stack direction="row" alignItems="center" justifyContent="space-between">
            <Box>
              <Typography variant="h5" sx={{ color: Colors.textPrimary }}>Contenido</Typography>
              <Typography variant="body2" sx={{ color: Colors.textSecondary }}>
                Items recomendados, eventos y sistema de éxito
              </Typography>
            </Box>
            <Stack direction="row" spacing={1.5}>
              <IconButton onClick={getItemsFromFB} sx={{ color: Colors.textSecondary }}>
                <MdRefresh size={20} />
              </IconButton>
              <Button
                variant="contained"
                startIcon={<MdAdd />}
                onClick={() => setAddOpen(true)}
                sx={{ backgroundColor: Colors.primary, color: Colors.white, textTransform: "none", borderRadius: 2 }}
              >
                Nuevo Item
              </Button>
            </Stack>
          </Stack>

          <Tabs
            value={tabValue}
            onChange={(_, v) => { setTabValue(v); setSearch(""); }}
            sx={{
              mt: 2,
              "& .MuiTab-root": { color: Colors.textSecondary, textTransform: "none" },
              "& .Mui-selected": { color: Colors.textPrimary },
              "& .MuiTabs-indicator": { backgroundColor: Colors.primary },
            }}
          >
            {TABS.map((t) => <Tab key={t} label={t} />)}
          </Tabs>
        </Paper>

        {/* Search */}
        <Stack direction="row" spacing={2} sx={{ mb: 2 }} alignItems="center">
          <TextField
            size="small"
            placeholder="Buscar por nombre..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            InputProps={{ startAdornment: <MdSearch style={{ marginRight: 6, color: Colors.textSecondary }} /> }}
            sx={{
              minWidth: 280,
              "& .MuiOutlinedInput-root": {
                backgroundColor: Colors.bgSecondary,
                color: Colors.textPrimary,
                "& fieldset": { borderColor: Colors.borderSecondary },
                "&:hover fieldset": { borderColor: Colors.borderPrimary },
              },
            }}
          />
          <Typography variant="caption" sx={{ color: Colors.textSecondary }}>
            {filtered.length} {filtered.length === 1 ? "resultado" : "resultados"}
          </Typography>
        </Stack>

        {loading && (
          <Box sx={{ display: "flex", justifyContent: "center", py: 6 }}>
            <CircularProgress sx={{ color: Colors.primary }} />
          </Box>
        )}

        {!loading && (
          <Paper sx={{ borderRadius: 3, backgroundColor: Colors.bgSecondary, border: `1px solid ${Colors.borderSecondary}`, overflow: "hidden" }}>
            {/* Table header */}
            <Box sx={{ px: 3, py: 1.5, backgroundColor: Colors.bgTertiary, borderBottom: `1px solid ${Colors.borderSecondary}` }}>
              <Stack direction="row" spacing={2} alignItems="center">
                <Box sx={{ flex: 3 }}>
                  <Typography variant="subtitle2" sx={{ color: Colors.textSecondary }}>Nombre</Typography>
                </Box>
                <Box sx={{ width: 60, flexShrink: 0 }}>
                  <Typography variant="subtitle2" sx={{ color: Colors.textSecondary }}>Imagen</Typography>
                </Box>
                <Box sx={{ width: 70, flexShrink: 0 }}>
                  <Typography variant="subtitle2" sx={{ color: Colors.textSecondary }}>Orden</Typography>
                </Box>
                {showDate && (
                  <Box sx={{ width: 100, flexShrink: 0 }}>
                    <Typography variant="subtitle2" sx={{ color: Colors.textSecondary }}>Fecha</Typography>
                  </Box>
                )}
                <Box sx={{ width: 90, flexShrink: 0 }}>
                  <Typography variant="subtitle2" sx={{ color: Colors.textSecondary }}>Acciones</Typography>
                </Box>
              </Stack>
            </Box>

            {filtered.length === 0 && (
              <Box sx={{ px: 3, py: 4, textAlign: "center" }}>
                <Typography sx={{ color: Colors.textSecondary }}>Sin resultados.</Typography>
              </Box>
            )}

            {filtered.map((item) => (
              <Box
                key={item.id}
                sx={{
                  px: 3, py: 1.75,
                  borderBottom: `1px solid ${Colors.borderSecondary}`,
                  "&:hover": { backgroundColor: Colors.bgTertiary },
                  transition: "background-color 0.15s",
                }}
              >
                <Stack direction="row" spacing={2} alignItems="center">
                  <Box sx={{ flex: 3 }}>
                    <Typography variant="body2" sx={{ color: Colors.textPrimary }}>{item.name || "—"}</Typography>
                  </Box>
                  <Box sx={{ width: 60, flexShrink: 0 }}>
                    {item.imageURL
                      ? <img src={item.imageURL} alt="" width={44} height={44} style={{ borderRadius: 4, objectFit: "cover", display: "block" }} />
                      : <Typography variant="caption" sx={{ color: Colors.textMuted }}>—</Typography>
                    }
                  </Box>
                  <Box sx={{ width: 70, flexShrink: 0 }}>
                    <Typography variant="body2" sx={{ color: Colors.textSecondary }}>{item.orderNumber ?? "—"}</Typography>
                  </Box>
                  {showDate && (
                    <Box sx={{ width: 100, flexShrink: 0 }}>
                      <Typography variant="caption" sx={{ color: Colors.textSecondary }}>{fmtDate(item.cdate)}</Typography>
                    </Box>
                  )}
                  <Stack direction="row" spacing={0.5} sx={{ width: 90, flexShrink: 0 }}>
                    <IconButton
                      size="small"
                      onClick={() => handleOpenEditModal(item.id)}
                      sx={{ color: Colors.textSecondary }}
                      title="Editar"
                    >
                      <MdEdit size={16} />
                    </IconButton>
                    <IconButton
                      size="small"
                      onClick={() => setConfirmDelete(item)}
                      sx={{ color: Colors.error }}
                      title="Eliminar"
                    >
                      <MdDelete size={16} />
                    </IconButton>
                  </Stack>
                </Stack>
              </Box>
            ))}
          </Paper>
        )}

        {/* Dialogs & modals */}
        <AddItemsDialog open={addOpen} onClose={() => setAddOpen(false)} refresh={getItemsFromFB} />

        {selectedItem && (
          <ItemsEditModal
            refresh={getItemsFromFB}
            data={selectedItem}
            open={editModalOpen}
            onClose={handleCloseEditModal}
            category={CATEGORY_MAP[tabValue]}
          />
        )}

        {/* Confirm delete dialog */}
        <Dialog
          open={!!confirmDelete}
          onClose={() => !deleting && setConfirmDelete(null)}
          PaperProps={{ sx: { backgroundColor: Colors.bgSecondary, border: `1px solid ${Colors.borderSecondary}` } }}
        >
          <DialogTitle sx={{ color: Colors.textPrimary }}>Confirmar eliminación</DialogTitle>
          <DialogContent>
            <Typography sx={{ color: Colors.textSecondary }}>
              ¿Eliminar "{confirmDelete?.name}"? Esta acción no se puede deshacer.
            </Typography>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setConfirmDelete(null)} disabled={deleting} sx={{ color: Colors.textSecondary }}>
              Cancelar
            </Button>
            <Button onClick={confirmDeleteAction} disabled={deleting} sx={{ color: Colors.error }}>
              {deleting ? "Eliminando..." : "Eliminar"}
            </Button>
          </DialogActions>
        </Dialog>

      </Container>
    </Box>
  );
}
